'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import { twJoin } from 'tailwind-merge';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { Swiper, SwiperSlide } from 'swiper/react';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import ButtonLink from '../../shared/ButtonLink';
import Container from '../../shared/Container';
import OptimizedImage from '../../shared/OptimizedImage';
import { ImageText5050Tile } from './types/ImageText5050Tile';
import './styles.css';

// Importing dynamicly fixed the Hydration Mismatch error
const ReactPlayer = dynamic(() => import('react-player'));

export default function ImageText5050({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: ImageText5050Tile;
  trackingPrefix?: string;
}>) {
  const { title, logo, description, image } = tile;
  const imageAspectRatio = tile['image-aspect-ratio']?.options?.[0]?.value;
  const imageAspectRatioMobile = tile['image-aspect-ratio-mobile']?.options?.[0]?.value;
  const imageBorder = tile['image-border']?.value;
  const tabs = tile.tabs?.chunks;
  const video = tile.video?.videos?.[0];

  let aspectRatioClass = '';
  switch (imageAspectRatio) {
    case 'full-height':
      aspectRatioClass = 'md:aspect-auto md:h-full';
      break;
    case '16:9':
      aspectRatioClass = 'md:aspect-[16/9]';
      break;
    case '9:16':
      aspectRatioClass = 'md:aspect-[9/16]';
      break;
    case '1:1':
      aspectRatioClass = 'md:aspect-[1/1]';
      break;
    case '4:3':
      aspectRatioClass = 'md:aspect-[4/3]';
      break;
    case '3:4':
      aspectRatioClass = 'md:aspect-[3/4]';
      break;
    default:
      aspectRatioClass = 'md:aspect-auto md:h-full';
      break;
  }

  let aspectRatioMobileClass = '';
  switch (imageAspectRatioMobile) {
    case '16:9':
      aspectRatioMobileClass = 'aspect-[16/9]';
      break;
    case '9:16':
      aspectRatioMobileClass = 'aspect-[9/16]';
      break;
    case '1:1':
      aspectRatioMobileClass = 'aspect-[1/1]';
      break;
    case '4:3':
      aspectRatioMobileClass = 'aspect-[4/3]';
      break;
    case '3:4':
      aspectRatioMobileClass = 'aspect-[3/4]';
      break;
    default:
      aspectRatioMobileClass = 'aspect-[16/9]';
      break;
  }

  return (
    <Container>
      <div
        className={twJoin(
          'md:grid md:grid-cols-2 md:gap-14',
          !imageAspectRatio && 'min-h-[500px]',
          imageAspectRatio === 'full-height' && 'min-h-[500px]',
        )}
      >
        <div
          className={twJoin(
            'relative mb-6 w-full overflow-hidden rounded-xl md:my-auto',
            aspectRatioClass,
            aspectRatioMobileClass,
            imageBorder && 'border-1 border border-neutral-10',
            tile['image-right']?.value && 'md:order-2',
          )}
        >
          <OptimizedImage
            imageComponent={image}
            fill
            className="col-span-1 object-cover"
            sizes="(max-width: 640px) 100vw, 50vw"
          />
          {video && (
            <ReactPlayer
              url={video.playlists?.[1]}
              playing
              loop
              muted
              width="100%"
              height="100%"
              className="video-cover"
              playsinline
            />
          )}
        </div>
        <div className={twJoin(' col-span-1 my-auto', !(tabs?.length > 0) && 'md:p-10 lg:p-12')}>
          <RichTextRenderer blocks={title} className="text-2xl sm:text-3xl lg:text-4xl" />
          <OptimizedImage
            imageComponent={logo}
            className="max-h-8"
            width={100}
            height={32}
            sizes="100px"
          />
          <RichTextRenderer
            blocks={description}
            className="custom-list mt-2 sm:text-md md:mt-4 md:text-lg lg:text-xl"
          />
          {/* Desktop */}
          <div className="hidden grid-cols-2 gap-6 sm:grid">
            {/* TODO: componentize this maping */}
            {tabs?.map((tab, index) => {
              const title = extractComponentFromChunk(tab, 'title', 'SingleLineContent');
              const description = extractComponentFromChunk(tab, 'description', 'RichTextContent');
              return (
                <div
                  key={`${index}5050 Tabs`}
                  className="min-h-full rounded-xl bg-white p-5 shadow-xl"
                >
                  <h4 className=" mb-2 text-md font-medium">{title?.text}</h4>
                  <RichTextRenderer blocks={description} />
                </div>
              );
            })}
          </div>
          {/* Mobile */}
          <Swiper spaceBetween={16} slidesPerView={1.2} className="image-text-5050 sm:!hidden">
            {tabs?.map((tab, index) => {
              const title = extractComponentFromChunk(tab, 'title', 'SingleLineContent');
              const description = extractComponentFromChunk(tab, 'description', 'RichTextContent');
              return (
                <SwiperSlide
                  key={`${index}5050 Tabs`}
                  className="!h-auto !min-h-full rounded-xl bg-white p-5 shadow-xl"
                >
                  <h4 className=" mb-2 text-md font-medium">{title?.text}</h4>
                  <RichTextRenderer blocks={description} />
                </SwiperSlide>
              );
            })}
          </Swiper>
          <ButtonLink
            className="mt-3 w-fit sm:mt-6"
            ctaComponent={tile.cta}
            trackingPrefix={trackingPrefix}
          />
          <RichTextRenderer
            blocks={tile.disclaimer}
            className=" mt-3 text-sm text-neutral-50 sm:mt-6"
          />
        </div>
      </div>
    </Container>
  );
}
